import {
  CalendarClock,
  Check,
  Clapperboard,
  EyeOff,
  Image as ImageIcon,
  Link2,
  LoaderCircle,
  MapPin,
  Pencil,
  Pin,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Type,
  UploadCloud,
  WifiOff,
  X,
} from "lucide-react";
import * as tus from "tus-js-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  embedProviderLabels,
  getEmbedProvider,
  newMutationId,
  pickCurrentEvent,
  sortOpenEvents,
} from "@vzlive/shared";
import { apiFetch, ApiError } from "../../lib/api";
import { fromDatetimeLocal, toDatetimeLocal } from "../../lib/datetime";
import { queueMutation } from "../../lib/offline-queue";
import { useAdmin } from "./AdminShell";

type PostKind = "text" | "image" | "embed" | "video";
type SubmitAction = "draft" | "publish" | "schedule";

interface UploadedAsset {
  id: string;
  originalFilename: string;
  originalUrl: string;
  state: string;
}

interface VideoAsset {
  id: string;
  state: "uploading" | "queued" | "processing" | "ready" | "failed" | "removed";
  title: string;
  originalFilename: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
}

interface UploadSession {
  asset: VideoAsset;
  upload: {
    videoId: string;
    libraryId: string;
    authorizationSignature: string;
    authorizationExpire: number;
    tusEndpoint: string;
  };
  maxBytes: number;
}

const TERMINAL_VIDEO_STATES = new Set(["ready", "failed", "removed"]);

const videoStateLabel = (state: string) => {
  switch (state) {
    case "ready":
      return "Video bereit";
    case "failed":
      return "Video fehlgeschlagen";
    case "removed":
      return "Video entfernt";
    case "uploading":
      return "Video wird hochgeladen";
    case "queued":
    case "processing":
      return "Video wird verarbeitet";
    default:
      return "Video";
  }
};

interface PostRow {
  post: {
    id: string;
    type: PostKind | "system";
    state: "draft" | "scheduled" | "published" | "unpublished";
    content: string;
    byline: string | null;
    publishAt: string | null;
    publishedAt: string | null;
    pinnedPosition: number | null;
    createdAt: string;
    embedUrl: string | null;
    embedProvider: string | null;
    embedData: {
      title?: string;
      html?: string;
      thumbnailUrl?: string;
    };
  };
  media: {
    id: string;
    originalUrl: string;
    originalFilename: string;
    credit: string | null;
    alt: string | null;
    derivatives: Array<{ url: string; width: number; format: string }>;
  } | null;
  video: VideoAsset | null;
}

const providerNames: Record<string, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  instagram: "Instagram",
  x: "X",
  mixlr: "Mixlr",
  bunny: "Bunny Stream",
};

// Der Admin erhält dieselben vom Resolver erzeugten Frames wie der Client;
// vor dem Rendern wird die Frame-Quelle gegen die Provider-Hostnames geprüft.
const embedPreviewSrc = (html: string | undefined) => {
  if (!html) return null;
  const match = html.match(/src="(https:\/\/[^"]+)"/);
  if (!match) return null;
  try {
    const src = match.at(1) ?? "";
    const hostname = new URL(src).hostname;
    const allowed = [
      "www.youtube-nocookie.com",
      "player.vimeo.com",
      "open.spotify.com",
      "platform.twitter.com",
      "www.instagram.com",
      "w.soundcloud.com",
      "mixlr.com",
      "www.mixlr.com",
      "events.mixlr.com",
      "player.mediadelivery.net",
    ];
    return allowed.includes(hostname) ? src : null;
  } catch {
    return null;
  }
};

const mediaThumbUrl = (row: PostRow) => {
  const derivatives = row.media?.derivatives
    .filter((item) => item.format !== "original")
    .sort((a, b) => a.width - b.width);
  return (
    derivatives?.find((item) => item.width >= 480)?.url ??
    derivatives?.at(-1)?.url
  );
};

const embedThumbnailUrl = (post: PostRow["post"]) => {
  const src = embedPreviewSrc(post.embedData.html);
  if (src) return null;
  return post.embedData.thumbnailUrl ?? null;
};

const postPreviewLabel = (row: PostRow) => {
  const { post, media, video } = row;
  const content = post.content.trim();
  if (content) return content.slice(0, 120);
  if (post.type === "image") return media?.originalFilename || "Bild";
  if (post.type === "video") return video?.title || "Video";
  if (post.type === "embed") {
    const provider = post.embedProvider
      ? (providerNames[post.embedProvider] ?? post.embedProvider)
      : null;
    return post.embedData.title || (provider ? `${provider}-Embed` : "Embed");
  }
  return "System-Beitrag";
};

const POST_PAGE_SIZE = 12;

export function AdminComposePage({
  embedded = false,
  initialEventId,
}: { embedded?: boolean; initialEventId?: string } = {}) {
  const { data, refresh } = useAdmin();
  const [kind, setKind] = useState<PostKind>("text");
  const [eventId, setEventId] = useState(
    initialEventId ??
      pickCurrentEvent(data.events)?.id ??
      data.events[0]?.id ??
      "",
  );
  const [asset, setAsset] = useState<UploadedAsset | null>(null);
  const [uploading, setUploading] = useState(false);
  const [videoAsset, setVideoAsset] = useState<VideoAsset | null>(null);
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const videoUploadRef = useRef<tus.Upload | null>(null);
  const [status, setStatus] = useState<
    "idle" | "sending" | "sent" | "queued" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const [recentPosts, setRecentPosts] = useState<PostRow[]>([]);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState("");
  const [postQuery, setPostQuery] = useState("");
  const [visiblePostCount, setVisiblePostCount] = useState(POST_PAGE_SIZE);

  const embedHint = useMemo(() => {
    if (kind !== "embed" || !embedUrl.trim()) return null;
    let parsed: URL;
    try {
      parsed = new URL(embedUrl.trim());
    } catch {
      return {
        tone: "error" as const,
        text: "Bitte eine vollständige http(s)-URL eingeben.",
      };
    }
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return {
        tone: "error" as const,
        text: "Bitte eine vollständige http(s)-URL eingeben.",
      };
    }
    if (!getEmbedProvider(embedUrl.trim())) {
      return {
        tone: "warn" as const,
        text: `Anbieter nicht unterstützt – der Beitrag wird als Link-Karte angezeigt. Erlaubte Player: ${embedProviderLabels.join(", ")}.`,
      };
    }
    return null;
  }, [kind, embedUrl]);

  const activeEvent = useMemo(
    () => data.events.find((event) => event.id === eventId),
    [data.events, eventId],
  );
  const loadPosts = useCallback(async () => {
    if (!eventId) {
      setRecentPosts([]);
      return;
    }
    try {
      setRecentPosts(
        await apiFetch<PostRow[]>(`/api/admin/events/${eventId}/posts`),
      );
    } catch {
      setRecentPosts([]);
    }
  }, [eventId]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    setPostQuery("");
    setVisiblePostCount(POST_PAGE_SIZE);
  }, [eventId]);

  const normalizedPostQuery = useMemo(
    () => postQuery.trim().toLowerCase(),
    [postQuery],
  );
  const filteredPosts = useMemo(
    () =>
      normalizedPostQuery
        ? recentPosts.filter(
            (row) =>
              row.post.content.toLowerCase().includes(normalizedPostQuery) ||
              (row.post.byline ?? "")
                .toLowerCase()
                .includes(normalizedPostQuery),
          )
        : recentPosts,
    [recentPosts, normalizedPostQuery],
  );
  const visiblePosts = filteredPosts.slice(0, visiblePostCount);
  const hasMorePosts = filteredPosts.length > visiblePosts.length;

  const upload = async (file: File) => {
    setUploading(true);
    setMessage("");
    const body = new FormData();
    body.append("file", file);
    try {
      const result = await apiFetch<UploadedAsset>("/api/admin/media", {
        method: "POST",
        body,
      });
      setAsset(result);
    } catch {
      setMessage(
        "Upload fehlgeschlagen. Prüfe die Verbindung und die Dateigröße.",
      );
    } finally {
      setUploading(false);
    }
  };

  // Video-Upload läuft resumable (TUS) direkt zu Bunny Stream – die API
  // legt nur das Video-Objekt an und signiert den Upload.
  const uploadVideo = async (file: File) => {
    setMessage("");
    setVideoProgress(0);
    try {
      const session = await apiFetch<UploadSession>(
        "/api/admin/video/upload-session",
        {
          method: "POST",
          body: JSON.stringify({
            filename: file.name,
            mimeType: file.type || "video/mp4",
            sizeBytes: file.size,
          }),
        },
      );
      setVideoAsset(session.asset);
      const tusUpload = new tus.Upload(file, {
        endpoint: session.upload.tusEndpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
        uploadSize: file.size,
        headers: {
          AuthorizationSignature: session.upload.authorizationSignature,
          AuthorizationExpire: String(session.upload.authorizationExpire),
          VideoId: session.upload.videoId,
          LibraryId: session.upload.libraryId,
        },
        metadata: {
          filetype: file.type || "video/mp4",
          title: file.name,
        },
        onError: () => {
          setMessage(
            "Video-Upload fehlgeschlagen. Der Upload kann bei stabiler Verbindung erneut gestartet werden.",
          );
          setVideoProgress(null);
          setVideoAsset((current) =>
            current ? { ...current, state: "failed" as const } : current,
          );
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setVideoProgress(
            Math.round((bytesUploaded / Math.max(bytesTotal, 1)) * 100),
          );
        },
        onSuccess: async () => {
          try {
            const synced = await apiFetch<VideoAsset>(
              `/api/admin/video/${session.asset.id}/complete`,
              { method: "POST" },
            );
            setVideoAsset(synced);
          } catch {
            // Webhook/Status-Refresh holt den Rest – Polling unten läuft.
          } finally {
            setVideoProgress(null);
          }
        },
      });
      videoUploadRef.current = tusUpload;
      tusUpload.start();
    } catch (error) {
      setVideoProgress(null);
      if (error instanceof ApiError) {
        if (error.code === "VIDEO_DISABLED") {
          setMessage("Video ist für diesen Tenant nicht aktiviert.");
        } else if (error.code === "VIDEO_NOT_CONFIGURED") {
          setMessage(
            "Video-Streaming ist auf diesem Server noch nicht konfiguriert (Bunny-Keys fehlen).",
          );
        } else if (error.code === "VIDEO_TOO_LARGE") {
          setMessage("Das Video überschreitet das Upload-Limit.");
        } else if (error.code === "UNSUPPORTED_MEDIA_TYPE") {
          setMessage(
            "Dieses Videoformat wird nicht unterstützt (MP4, MOV, WebM …).",
          );
        } else {
          setMessage(`Video-Upload fehlgeschlagen: ${error.message}`);
        }
      } else {
        setMessage("Video-Upload fehlgeschlagen.");
      }
    }
  };

  const cancelVideoUpload = () => {
    videoUploadRef.current?.abort();
    videoUploadRef.current = null;
    if (videoAsset) {
      // Session verwerfen, sonst bleibt in Bunny ein leeres Video liegen
      // (Storage-Kosten). Fehlschlag ist unkritisch – Bunny räumt
      // unvollständige TUS-Uploads selbst auf.
      void apiFetch(`/api/admin/video/${videoAsset.id}`, {
        method: "DELETE",
      }).catch(() => undefined);
    }
    setVideoAsset(null);
    setVideoProgress(null);
  };

  // Encoding-Fortschritt nachziehen, bis Bunny fertig ist – der Beitrag
  // kann längst gepostet sein, der Player erscheint mit "ready".
  const videoState = videoAsset?.state;
  useEffect(() => {
    if (!videoAsset || TERMINAL_VIDEO_STATES.has(videoState ?? "")) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const synced = await apiFetch<VideoAsset>(
          `/api/admin/video/${videoAsset.id}`,
        );
        if (!cancelled) setVideoAsset(synced);
      } catch {
        /* Netzwerkprobleme: nächster Tick probiert es erneut. */
      }
    };
    const interval = window.setInterval(() => void tick(), 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [videoAsset, videoState]);

  const submit = async (element: HTMLFormElement, action: SubmitAction) => {
    const form = new FormData(element);
    const clientMutationId = newMutationId();
    const payload = {
      eventId,
      type: kind,
      content: String(form.get("content") ?? ""),
      mediaAssetId: kind === "image" ? asset?.id : undefined,
      videoAssetId: kind === "video" ? videoAsset?.id : undefined,
      embedUrl:
        kind === "embed" ? String(form.get("embedUrl") ?? "") : undefined,
      byline: String(form.get("byline") ?? "") || undefined,
      publishAt:
        action === "schedule"
          ? (fromDatetimeLocal(String(form.get("publishAt") ?? "")) ?? "")
          : undefined,
      pinnedPosition: form.get("pinned") === "on" ? 0 : undefined,
      action,
      clientMutationId,
    };
    setStatus("sending");
    setMessage("");
    try {
      if (kind === "image" && asset) {
        await apiFetch(`/api/admin/media/${asset.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            credit: String(form.get("credit") ?? "") || null,
            alt: String(form.get("alt") ?? "") || null,
          }),
        });
      }
      await apiFetch("/api/admin/posts", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setStatus("sent");
      setMessage(
        action === "publish"
          ? "Beitrag ist live."
          : action === "schedule"
            ? "Beitrag ist eingeplant."
            : "Entwurf ist gesichert.",
      );
      element.reset();
      setAsset(null);
      setVideoAsset(null);
      setVideoProgress(null);
      setEmbedUrl("");
      await loadPosts();
      await refresh();
      window.setTimeout(() => setStatus("idle"), 2_500);
    } catch (error) {
      // Antworten des Servers sind keine Offline-Fälle – sie werden als
      // Fehler angezeigt statt im Offline-Queue zu verschwinden.
      if (error instanceof ApiError) {
        setStatus("error");
        setMessage(
          error.code === "EMBED_INVALID"
            ? "Die Embed-URL konnte nicht verarbeitet werden. Prüfe die Adresse."
            : `Der Beitrag konnte nicht gesendet werden: ${error.message}`,
        );
        return;
      }
      if (kind === "text" || kind === "embed") {
        await queueMutation({
          id: clientMutationId,
          path: "/api/admin/posts",
          method: "POST",
          body: payload,
        });
        setStatus("queued");
        setMessage(
          "Lokal vorgemerkt – wird automatisch gesendet, sobald die Verbindung zurück ist.",
        );
      } else {
        setStatus("error");
        setMessage("Der Beitrag konnte nicht gesendet werden.");
      }
    }
  };

  const unpublish = async (postId: string) => {
    await apiFetch(`/api/admin/posts/${postId}/unpublish`, {
      method: "POST",
    });
    await loadPosts();
    flashPostStatus("Beitrag wurde zurückgezogen.");
  };

  const updatePost = async (
    postId: string,
    row: PostRow,
    element: HTMLFormElement,
    action: "save" | "publish",
  ) => {
    const form = new FormData(element);
    const publishAtValue = String(form.get("publishAt") ?? "");
    const publishAtIso = fromDatetimeLocal(publishAtValue);
    const embedUrlValue =
      row.post.type === "embed" ? String(form.get("embedUrl") ?? "") : "";
    // Bei veröffentlichten Beiträgen schreibt die im Dialog gezeigte Zeit
    // den tatsächlichen Veröffentlichungszeitpunkt (publishedAt), damit das
    // öffentliche Frontend sie übernimmt; unveröffentlichte steuern damit
    // den Planungszeitpunkt (publishAt). Leeres Feld ändert nichts.
    const publishedAtPatch =
      row.post.state === "published" && publishAtIso
        ? { publishedAt: publishAtIso }
        : {};
    await apiFetch(`/api/admin/posts/${postId}`, {
      method: "PATCH",
      body: JSON.stringify({
        content: String(form.get("content") ?? ""),
        byline: String(form.get("byline") ?? "") || null,
        publishAt: publishAtIso,
        ...publishedAtPatch,
        pinnedPosition: form.get("pinned") === "on" ? 0 : null,
        ...(embedUrlValue ? { embedUrl: embedUrlValue } : {}),
        ...(action === "publish" ? { state: "published" } : {}),
      }),
    });
    if (row.post.type === "image" && row.media) {
      await apiFetch(`/api/admin/media/${row.media.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          credit: String(form.get("credit") ?? "") || null,
          alt: String(form.get("alt") ?? "") || null,
        }),
      });
    }
    setEditingPostId(null);
    await loadPosts();
    await refresh();
    flashPostStatus(
      action === "publish"
        ? "Beitrag ist wieder live."
        : "Beitrag aktualisiert.",
    );
  };

  const flashPostStatus = (value: string) => {
    setStatus("sent");
    setMessage(value);
    window.setTimeout(() => setStatus("idle"), 2_500);
  };

  return (
    <div
      className={`admin-page compose-page ${embedded ? "dashboard-composer" : ""}`}
    >
      <header className="admin-page-header">
        <div>
          {embedded ? <h2>Update schreiben</h2> : <h1>Neuer Beitrag</h1>}
          {!embedded && (
            <p>
              Veröffentliche sofort, plane voraus oder sichere einen Entwurf.
            </p>
          )}
        </div>
        {!embedded && activeEvent && (
          <div className="current-event">
            <i className={activeEvent.status === "live" ? "is-live" : ""} />
            <div>
              <small>
                {activeEvent.status === "live"
                  ? "Gerade live"
                  : "Ausgewähltes Event"}
              </small>
              <strong>{activeEvent.title}</strong>
            </div>
          </div>
        )}
      </header>

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(event.currentTarget, "publish");
        }}
      >
        <div className="composer__main">
          <div className="composer-event-select" hidden={embedded}>
            <label htmlFor="event">Event</label>
            <select
              id="event"
              value={eventId}
              onChange={(event) => setEventId(event.target.value)}
              required
            >
              {sortOpenEvents(data.events).map((event) => (
                <option value={event.id} key={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </div>

          <div className="composer-kind" role="group" aria-label="Beitragstyp">
            {(
              [
                ["text", Type, "Text"],
                ["image", ImageIcon, "Bild"],
                ...(data.tenant.videoEnabled
                  ? ([["video", Clapperboard, "Video"]] as const)
                  : []),
                ["embed", Link2, "Embed"],
              ] as const
            ).map(([value, Icon, label]) => (
              <button
                type="button"
                className={kind === value ? "active" : ""}
                aria-pressed={kind === value}
                onClick={() => setKind(value)}
                key={value}
              >
                <Icon size={18} /> {label}
              </button>
            ))}
          </div>

          {kind === "image" && (
            <div className={`media-drop ${asset ? "media-drop--ready" : ""}`}>
              {asset ? (
                <>
                  <img src={asset.originalUrl} alt="" />
                  <div>
                    <Check />
                    <strong>{asset.originalFilename}</strong>
                    <small>Upload fertig · Verarbeitung läuft</small>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAsset(null)}
                    aria-label="Bild entfernen"
                  >
                    <X />
                  </button>
                </>
              ) : (
                <label>
                  {uploading ? (
                    <LoaderCircle className="spin" />
                  ) : (
                    <UploadCloud />
                  )}
                  <strong>
                    {uploading
                      ? "Bild wird hochgeladen…"
                      : "Foto auswählen oder hier ablegen"}
                  </strong>
                  <small>JPEG, PNG, WebP oder HEIC · maximal 25 MB</small>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    disabled={uploading}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void upload(file);
                    }}
                  />
                </label>
              )}
            </div>
          )}

          {kind === "video" && (
            <div
              className={`media-drop ${videoAsset ? "media-drop--ready" : ""}`}
            >
              {videoAsset ? (
                <>
                  {videoAsset.thumbnailUrl ? (
                    <img src={videoAsset.thumbnailUrl} alt="" />
                  ) : (
                    <span className="media-drop__glyph" aria-hidden="true">
                      <Clapperboard size={22} />
                    </span>
                  )}
                  <div>
                    {videoProgress !== null ? (
                      <>
                        <LoaderCircle className="spin" />
                        <strong>Upload läuft … {videoProgress}%</strong>
                        <small>{videoAsset.originalFilename}</small>
                      </>
                    ) : videoAsset.state === "ready" ? (
                      <>
                        <Check />
                        <strong>{videoAsset.title}</strong>
                        <small>
                          Bereit
                          {videoAsset.durationSeconds
                            ? ` · ${Math.round(videoAsset.durationSeconds / 60)}:${String(videoAsset.durationSeconds % 60).padStart(2, "0")} Min.`
                            : ""}
                        </small>
                      </>
                    ) : videoAsset.state === "failed" ? (
                      <>
                        <X />
                        <strong>Verarbeitung fehlgeschlagen</strong>
                        <small>Bitte neu hochladen.</small>
                      </>
                    ) : (
                      <>
                        <LoaderCircle className="spin" />
                        <strong>Bunny verarbeitet das Video …</strong>
                        <small>
                          {videoAsset.originalFilename} · Du kannst den Beitrag
                          schon senden
                        </small>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={cancelVideoUpload}
                    aria-label="Video entfernen"
                  >
                    <Trash2 />
                  </button>
                </>
              ) : (
                <label>
                  <UploadCloud />
                  <strong>Video auswählen oder hier ablegen</strong>
                  <small>
                    MP4, MOV oder WebM · direkt zu Bunny Stream (resumable)
                  </small>
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/*"
                    disabled={videoProgress !== null}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void uploadVideo(file);
                    }}
                  />
                </label>
              )}
            </div>
          )}

          {kind === "embed" && (
            <>
              <label className="composer-field">
                <span>URL des externen Inhalts</span>
                <div className="input-with-icon">
                  <Link2 />
                  <input
                    name="embedUrl"
                    type="url"
                    placeholder="https://youtube.com/watch?v=…"
                    value={embedUrl}
                    onChange={(event) => setEmbedUrl(event.target.value)}
                    required
                  />
                </div>
                <small>
                  {embedHint
                    ? embedHint.text
                    : "YouTube, Vimeo, Spotify, SoundCloud, Instagram, X (Twitter) oder Mixlr."}
                </small>
              </label>
              {embedHint && (
                <p
                  className={`composer-warning${embedHint.tone === "warn" ? " composer-warning--warn" : ""}`}
                  role={embedHint.tone === "error" ? "alert" : "status"}
                >
                  {embedHint.text}
                </p>
              )}
            </>
          )}

          <label className="composer-copy">
            <span>Text</span>
            <textarea
              name="content"
              rows={kind === "text" ? 8 : 5}
              placeholder="Was passiert gerade?"
              required={kind === "text"}
              maxLength={20_000}
            />
          </label>
        </div>

        <aside className="composer__options">
          <h2>Details</h2>
          <label>
            <span>
              Byline <small>optional</small>
            </span>
            <input
              name="byline"
              placeholder="z. B. Maik vor Ort"
              maxLength={100}
            />
          </label>
          {kind === "image" && asset && (
            <>
              <label>
                <span>
                  Bildnachweis <small>optional</small>
                </span>
                <input name="credit" placeholder="Foto: Name / Quelle" />
              </label>
              <label>
                <span>
                  Alt-Text <small>Barrierefreiheit</small>
                </span>
                <textarea
                  name="alt"
                  rows={3}
                  maxLength={1_000}
                  placeholder="Was ist auf dem Bild sachlich zu sehen?"
                />
              </label>
            </>
          )}
          <label>
            <span>
              <CalendarClock size={15} /> Veröffentlichung
            </span>
            <input name="publishAt" type="datetime-local" />
          </label>
          <label className="switch-row">
            <span>
              <Pin size={15} /> Oben anheften
            </span>
            <input name="pinned" type="checkbox" role="switch" />
          </label>
          {activeEvent?.venue && (
            <p className="composer-venue">
              <MapPin size={15} /> {activeEvent.venue}
            </p>
          )}
          {status !== "idle" && (
            <div
              className={`composer-message composer-message--${status}`}
              role={status === "error" ? "alert" : "status"}
            >
              {status === "queued" ? (
                <WifiOff />
              ) : status === "sent" ? (
                <Check />
              ) : status === "sending" ? (
                <LoaderCircle className="spin" />
              ) : (
                <X />
              )}
              <span>{message || "Wird gesendet…"}</span>
            </div>
          )}
        </aside>

        <footer className="composer__actions">
          <button
            type="button"
            onClick={(event) => void submit(event.currentTarget.form!, "draft")}
            disabled={
              status === "sending" ||
              embedHint?.tone === "error" ||
              (kind === "image" && !asset) ||
              (kind === "video" &&
                (!videoAsset ||
                  videoProgress !== null ||
                  videoAsset.state === "failed"))
            }
          >
            <Save size={17} /> Entwurf
          </button>
          <button
            type="button"
            onClick={(event) =>
              void submit(event.currentTarget.form!, "schedule")
            }
            disabled={
              status === "sending" ||
              embedHint?.tone === "error" ||
              (kind === "image" && !asset) ||
              (kind === "video" &&
                (!videoAsset ||
                  videoProgress !== null ||
                  videoAsset.state === "failed"))
            }
          >
            <CalendarClock size={17} /> Planen
          </button>
          <button
            type="submit"
            className="button-live"
            disabled={
              status === "sending" ||
              !eventId ||
              (kind === "image" && !asset) ||
              (kind === "video" &&
                (!videoAsset ||
                  videoProgress !== null ||
                  videoAsset.state === "failed")) ||
              embedHint?.tone === "error"
            }
          >
            <Send size={17} /> Jetzt senden
          </button>
        </footer>
      </form>

      <section className="recent-posts">
        <header>
          <h2>Letzte Beiträge</h2>
          <span>{recentPosts.length}</span>
        </header>
        {recentPosts.length > 0 && (
          <div className="recent-posts__search">
            <input
              type="search"
              placeholder="Beitrag suchen …"
              value={postQuery}
              onChange={(event) => {
                setPostQuery(event.target.value);
                setVisiblePostCount(POST_PAGE_SIZE);
              }}
              aria-label="Beiträge durchsuchen"
            />
          </div>
        )}
        <div>
          {visiblePosts.map((row) => {
            const thumb = row.post.type === "image" ? mediaThumbUrl(row) : null;
            const embedThumb =
              row.post.type === "embed" ? embedThumbnailUrl(row.post) : null;
            const videoThumb =
              row.post.type === "video" ? row.video?.thumbnailUrl : null;
            const previewUrl = thumb ?? embedThumb ?? videoThumb ?? null;
            return (
              <article key={row.post.id}>
                <span className={`post-state post-state--${row.post.state}`}>
                  {row.post.state}
                </span>
                <div
                  className={`recent-posts__preview${previewUrl ? "" : " recent-posts__preview--empty"}`}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="" loading="lazy" />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="recent-posts__preview-glyph"
                    >
                      {row.post.type === "embed" ? (
                        <Link2 size={18} />
                      ) : row.post.type === "video" ? (
                        <Clapperboard size={18} />
                      ) : (
                        <ImageIcon size={18} />
                      )}
                    </span>
                  )}
                </div>
                <div>
                  <strong>
                    {row.post.content.trim().slice(0, 120) ||
                      postPreviewLabel(row)}
                  </strong>
                  <small>
                    {row.post.byline ? `${row.post.byline} · ` : ""}
                    {row.post.type === "video" && row.video
                      ? `${videoStateLabel(row.video.state)} · `
                      : ""}
                    {row.post.type === "embed" &&
                      row.post.embedProvider &&
                      `${providerNames[row.post.embedProvider] ?? row.post.embedProvider} · `}
                    {new Intl.DateTimeFormat("de", {
                      dateStyle: "short",
                      timeStyle: "short",
                    }).format(
                      new Date(
                        row.post.publishedAt ??
                          row.post.publishAt ??
                          row.post.createdAt,
                      ),
                    )}
                  </small>
                </div>
                <div className="recent-posts__actions">
                  <button
                    type="button"
                    onClick={() =>
                      void setEditingPostId(
                        editingPostId === row.post.id ? null : row.post.id,
                      )
                    }
                    aria-label="Beitrag bearbeiten"
                  >
                    <Pencil size={17} /> Bearbeiten
                  </button>
                  {row.post.state === "published" && (
                    <button
                      type="button"
                      onClick={() => void unpublish(row.post.id)}
                      aria-label="Beitrag zurückziehen"
                    >
                      <EyeOff size={17} /> Zurückziehen
                    </button>
                  )}
                </div>
                {editingPostId === row.post.id && (
                  <form
                    className="post-edit-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void updatePost(
                        row.post.id,
                        row,
                        event.currentTarget,
                        "save",
                      );
                    }}
                  >
                    {row.post.type === "image" && mediaThumbUrl(row) && (
                      <figure className="post-edit-form__preview">
                        <img
                          src={mediaThumbUrl(row)!}
                          alt={row.media?.alt ?? ""}
                          loading="lazy"
                        />
                      </figure>
                    )}
                    {row.post.type === "image" && row.media && (
                      <>
                        <label>
                          <span>
                            Bildnachweis <small>optional</small>
                          </span>
                          <input
                            name="credit"
                            placeholder="Foto: Name / Quelle"
                            defaultValue={row.media.credit ?? ""}
                            maxLength={250}
                          />
                        </label>
                        <label>
                          <span>
                            Alt-Text <small>Barrierefreiheit</small>
                          </span>
                          <textarea
                            name="alt"
                            rows={3}
                            defaultValue={row.media.alt ?? ""}
                            maxLength={1_000}
                          />
                        </label>
                      </>
                    )}
                    {row.post.type === "embed" && row.post.embedUrl && (
                      <label>
                        <span>Embed-URL</span>
                        <input
                          name="embedUrl"
                          type="url"
                          defaultValue={row.post.embedUrl}
                          required
                        />
                      </label>
                    )}
                    {row.post.type === "embed" && row.post.embedUrl && (
                      <div className="post-edit-form__preview post-edit-form__preview--embed">
                        {embedPreviewSrc(row.post.embedData.html) ? (
                          <iframe
                            src={embedPreviewSrc(row.post.embedData.html)!}
                            title={row.post.embedData.title ?? "Embed"}
                            loading="lazy"
                          />
                        ) : (
                          <span className="recent-posts__preview-glyph">
                            <Link2 size={18} />
                          </span>
                        )}
                        <a
                          href={row.post.embedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {row.post.embedData.title ??
                            (row.post.embedProvider
                              ? (providerNames[row.post.embedProvider] ??
                                row.post.embedProvider)
                              : row.post.embedUrl)}
                        </a>
                      </div>
                    )}
                    <label>
                      <span>Text</span>
                      <textarea
                        name="content"
                        rows={4}
                        defaultValue={row.post.content}
                        maxLength={20_000}
                      />
                    </label>
                    <label>
                      <span>Byline</span>
                      <input
                        name="byline"
                        defaultValue={row.post.byline ?? ""}
                        maxLength={100}
                      />
                    </label>
                    <label>
                      <span>Veröffentlichung</span>
                      <input
                        name="publishAt"
                        type="datetime-local"
                        defaultValue={toDatetimeLocal(
                          row.post.publishAt ?? row.post.publishedAt,
                        )}
                      />
                    </label>
                    <label className="switch-row">
                      <span>Oben anheften</span>
                      <input
                        name="pinned"
                        type="checkbox"
                        role="switch"
                        defaultChecked={row.post.pinnedPosition !== null}
                      />
                    </label>
                    <div className="post-edit-form__actions">
                      <button type="submit">
                        <Save size={17} /> Speichern
                      </button>
                      {row.post.state !== "published" && (
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={(event) =>
                            void updatePost(
                              row.post.id,
                              row,
                              event.currentTarget.form as HTMLFormElement,
                              "publish",
                            )
                          }
                        >
                          <RotateCcw size={17} /> Veröffentlichen
                        </button>
                      )}
                      <button
                        type="button"
                        className="button-secondary"
                        onClick={() => setEditingPostId(null)}
                      >
                        <X size={17} /> Abbrechen
                      </button>
                    </div>
                  </form>
                )}
              </article>
            );
          })}
          {hasMorePosts && (
            <button
              type="button"
              className="recent-posts__more"
              onClick={() =>
                setVisiblePostCount((count) => count + POST_PAGE_SIZE)
              }
            >
              Ältere Beiträge anzeigen (
              {filteredPosts.length - visiblePosts.length})
            </button>
          )}
          {filteredPosts.length === 0 && recentPosts.length > 0 && (
            <p>Keine Beiträge gefunden für „{postQuery.trim()}“.</p>
          )}
          {!recentPosts.length && <p>Noch keine Beiträge in diesem Event.</p>}
        </div>
      </section>
    </div>
  );
}
